import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg" | "icon";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

/** cp button system (SDR): default = surface + line border + hover accent
 * lift; primary = accent gradient; soft = surface-2; danger = rose wash. */
const variantStyles: Record<Variant, string> = {
  primary: "cp-btn--primary",
  secondary: "cp-btn--soft",
  ghost: "cp-btn--ghost",
  danger: "cp-btn--danger",
  outline: "",
};

/** Tailwind utilities (utilities layer) override the cp-btn base paddings. */
const sizeStyles: Record<Size, string> = {
  sm: "px-2.5 py-[5px] text-xs gap-1.5",
  md: "",
  lg: "px-4 py-2.5 text-[13.5px] gap-2",
  icon: "h-9 w-9 p-0",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", className, loading, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "cp-btn focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--cp-canvas)]",
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        children
      )}
    </button>
  );
});
